import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Mail, Edit3, Trash2, Search, Calendar } from 'lucide-react';
import SideNavbar from '../SideNavbar';
import { useAppSelector } from '../../redux/store';

// firebase
import { app, db, functions } from "../../firebaseConfig";
import { doc, getDoc, collection, query, where, getDocs, Timestamp, limit, addDoc, updateDoc, getCountFromServer, DocumentData, documentId, deleteDoc } from 'firebase/firestore';
import { getAuth } from "firebase/auth";

interface EmailTemplate {
  id?: string;
  name: string;
  subject: string;
  content: string;
  category: string;
  createdAt: Date;
  lastModified: Date;
}

const GmailTemplates = () => {

  const navigate = useNavigate();
  const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);

  const handleGoBack = () => {
    navigate('/integrations/gmail', { replace: true });
  };

  const [userData, setUserData] = useState<{ companyId: string } | null>(null);

  // get user data
  useEffect(() => {
    const fetchUserData = async () => {
      const user = getAuth().currentUser;
      if (user) {
        const userDocRef = doc(db, `users/${user.uid}`);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserData({ companyId: userData.companyId });
        } else {
          console.error('User data not found');
        }
      } else {
        console.error('No user is currently logged in');
      }
    };

    fetchUserData();
  }, []);

  const [templates, setTemplates] = useState<EmailTemplate[]>([]); // Initialize with an empty array

  const [exTemplates, setExTemplates] = useState<EmailTemplate[]>([
    {
      id: '1',
      name: 'Welcome Email',
      subject: 'Welcome to our platform!',
      content: 'Dear {{name}},\n\nWelcome to our amazing platform! We\'re excited to have you on board.\n\nBest regards,\nThe Team',
      category: 'Onboarding',
      createdAt: new Date('2024-01-15'),
      lastModified: new Date('2024-01-15')
    },
    {
      id: '2',
      name: 'Password Reset',
      subject: 'Reset your password',
      content: 'Hi {{name}},\n\nYou requested a password reset. Click the link below to reset your password:\n\n{{resetLink}}\n\nIf you didn\'t request this, please ignore this email.',
      category: 'Security',
      createdAt: new Date('2024-01-10'),
      lastModified: new Date('2024-01-12')
    }
  ]);

  // Fetch templates from Firestore
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!userData) return;
      try {
        const templatesRef = collection(db, `companies/${userData.companyId}/emailTemplates`);
        const templatesQuery = query(templatesRef);
        const querySnapshot = await getDocs(templatesQuery);
        const fetchedTemplates: EmailTemplate[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as EmailTemplate;
          fetchedTemplates.push({
            id: doc.id,
            name: data.name,
            subject: data.subject,
            content: data.content,
            category: data.category,
            createdAt: data.createdAt,
            lastModified: data.lastModified
          });
        });
        setTemplates(fetchedTemplates);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };

    fetchTemplates();
  }, [userData]);

  const [saveLoading, setSaveLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    subject: '',
    content: '',
    category: 'General'
  });

  const categories = ['All', 'Onboarding', 'Security', 'Marketing', 'Support', 'General'];

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveLoading(true);

    if (!formData.name.trim() || !formData.subject.trim() || !formData.content.trim()) {
      alert('Please fill in all required fields');
      setSaveLoading(false);
      return;
    }

    try {

      const newTemplate: EmailTemplate = {
        // id: Date.now().toString(),
        name: formData.name,
        subject: formData.subject,
        content: formData.content,
        category: formData.category,
        createdAt: new Date(),
        lastModified: new Date()
      };

      // Add the new template to the db in collection 'emailTemplates'
      const emailTemplateRef = await addDoc(collection(db, `companies/${userData?.companyId}/emailTemplates`), {
        ...newTemplate,
        userId: getAuth().currentUser?.uid,
        createdAt: Timestamp.fromDate(newTemplate.createdAt),
        lastModified: Timestamp.fromDate(newTemplate.lastModified)
      });

      // Set the id of the new template
      newTemplate.id = emailTemplateRef.id;

      setTemplates([...templates, newTemplate]);
      setFormData({ id: '', name: '', subject: '', content: '', category: 'General' });
      setShowCreateForm(false);
      setSaveLoading(false);
      alert('Template created successfully!');
    } catch (error) {
      console.error('Error creating template:', error);
      setSaveLoading(false);
      alert('Failed to create template. Please try again.');
    }
  };

  const handleEditTemplateAction = (id: string) => {
    console.log('Editing template with ID:', id);

    const templateToEdit = templates.find(template => template.id === id);
    if (templateToEdit) {
      setFormData({
        id: templateToEdit.id ?? '',
        name: templateToEdit.name,
        subject: templateToEdit.subject,
        content: templateToEdit.content,
        category: templateToEdit.category
      });
      setShowCreateForm(true);
      setIsEditing(true);
    }
  };

  const handleEditTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveLoading(true);
    if (!formData.name.trim() || !formData.subject.trim() || !formData.content.trim()) {
      alert('Please fill in all required fields');
      setSaveLoading(false);
      return;
    }
    try {
      const updatedTemplate: EmailTemplate = {
        id: formData.id, // Assuming formData has an id field for the template being edited
        name: formData.name,
        subject: formData.subject,
        content: formData.content,
        category: formData.category,
        createdAt: new Date(),
        lastModified: new Date()
      };
      const templateRef = doc(db, `companies/${userData?.companyId}/emailTemplates/${formData.id}`);
      await updateDoc(templateRef, {
        name: updatedTemplate.name,
        subject: updatedTemplate.subject,
        content: updatedTemplate.content,
        category: updatedTemplate.category,
        lastModified: Timestamp.fromDate(updatedTemplate.lastModified)
      });

      setTemplates(templates.map(template => template.id === formData.id ? updatedTemplate : template));
      setFormData({ id: '', name: '', subject: '', content: '', category: 'General' });
      setShowCreateForm(false);
      setIsEditing(false);
      setSaveLoading(false);
      alert('Template updated successfully!');
    } catch (error) {
      console.error('Error updating template:', error);
      setSaveLoading(false);
      alert('Failed to update template. Please try again.');
    }
  };

  const handleDeleteTemplate = (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {

      const templateRef = doc(db, `companies/${userData?.companyId}/emailTemplates/${id}`);
      // Delete the template from Firestore
      deleteDoc(templateRef)
        .then(() => {
          console.log('Template deleted successfully');
          alert('Template deleted successfully');
        })
        .catch((error) => {
          console.error('Error deleting template:', error);
          alert('Failed to delete template. Please try again.');
        });
      setTemplates(templates.filter(template => template.id !== id));
    }
  };

  const formatDate = (date: Date | Timestamp) => {

    if (date instanceof Timestamp) {
      date = date.toDate();
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setIsEditing(false);
    setFormData({ id: '', name: '', subject: '', content: '', category: 'General' });
  };

  return (
    <div className="min-h-screen bg-gray-200">
      <div className="fixed top-16 left-0 h-full z-10">
        <SideNavbar />
      </div>
      <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
        <div className="bg-white p-8 rounded-3xl shadow-md">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <button
                onClick={handleGoBack}
                className="text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Gmail Integration
              </button>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Mail className="h-8 w-8 text-blue-600" />
                Email Templates
              </h1>
              <p className="text-gray-600 mt-1">Manage your email templates efficiently</p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Create Template
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search templates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Templates List */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Templates ({filteredTemplates.length})
              </h2>
            </div>

            {filteredTemplates.length === 0 ? (
              <div className="p-12 text-center">
                <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
                <p className="text-gray-600">
                  {searchTerm || selectedCategory !== 'All'
                    ? 'Try adjusting your search or filter criteria'
                    : 'Create your first email template to get started'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredTemplates.map(template => (
                  <div key={template.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {template.category}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2 font-medium">{template.subject}</p>
                        <div className="text-sm text-gray-500 mb-3">
                          {template.content.substring(0, 150)}
                          {template.content.length > 150 && '...'}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Created: {formatDate(template.createdAt)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Modified: {formatDate(template.lastModified)}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEditTemplateAction(template.id!)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Edit3 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id!)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create Template Modal */}
          {showCreateForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Create New Template</h2>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter template name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {categories.filter(cat => cat !== 'All').map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject Line <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter email subject"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter email content."
                        required
                      />
                    </div>

                    <div className="text-sm text-gray-500">
                      {/* <p>💡 <strong>Tip:</strong> Use double curly braces for variables (e.g., {'{{name}}'}, {'{{email}}'})</p> */}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => handleCancelCreate()}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    {saveLoading ? (
                      <button
                        type="button"
                        disabled
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg opacity-50 cursor-not-allowed"
                      >
                        Saving...
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={isEditing ? handleEditTemplate : handleCreateTemplate}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        {isEditing ? 'Update Template' : 'Create Template'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GmailTemplates
